import React, { useState, useEffect } from 'react'
import { Type, Upload, Bookmark, Play, Save, Trash2, Edit3 } from 'lucide-react'
import { useApp } from '../context/AppContext.jsx'
import FileUpload from '../components/FileUpload.jsx'
import AudioModal from '../components/AudioModal.jsx'
import { generateSpeech } from '../utils/speechSynthesis.js'
import toast from 'react-hot-toast'

function TextInput() {
  const {
    textInput,
    updateTextInput,
    uploadedText,
    updateUploadedText,
    bookmarks,
    addBookmark,
    deleteBookmark,
    updateBookmark,
    voiceSettings,
    addAudioFile,
    isLoading,
    setIsLoading
  } = useApp()

  const [showFileUpload, setShowFileUpload] = useState(false)
  const [showBookmarks, setShowBookmarks] = useState(false)
  const [showAudioModal, setShowAudioModal] = useState(false)
  const [generatedAudio, setGeneratedAudio] = useState(null)
  const [editingBookmark, setEditingBookmark] = useState(null)
  const [bookmarkTitle, setBookmarkTitle] = useState('')
  const [wordCount, setWordCount] = useState(0)
  const [charCount, setCharCount] = useState(0)

  // Calculate word and character count
  useEffect(() => {
    const text = textInput || uploadedText
    setCharCount(text.length)
    setWordCount(text.trim() ? text.trim().split(/\s+/).length : 0)
  }, [textInput, uploadedText])

  const currentText = textInput || uploadedText

  const handleTextChange = (e) => {
    updateTextInput(e.target.value)
    if (uploadedText) {
      updateUploadedText('')
    }
  }

  const handleFileUpload = (content) => {
    updateUploadedText(content)
    updateTextInput('')
    setShowFileUpload(false)
    toast.success('File uploaded successfully!')
  }

  const handleBookmarkSave = () => {
    if (!currentText.trim()) {
      toast.error('Please enter some text to bookmark')
      return
    }

    if (editingBookmark) {
      updateBookmark(editingBookmark.id, {
        title: bookmarkTitle || `Bookmark ${bookmarks.length + 1}`,
        text: currentText
      })
      setEditingBookmark(null)
    } else {
      addBookmark(currentText, bookmarkTitle || `Bookmark ${bookmarks.length + 1}`)
    }

    setBookmarkTitle('')
  }

  const handleBookmarkLoad = (bookmark) => {
    updateTextInput(bookmark.text)
    updateUploadedText('')
    toast.success('Bookmark loaded!')
  }

  const handleBookmarkEdit = (bookmark) => {
    setEditingBookmark(bookmark)
    setBookmarkTitle(bookmark.title)
    updateTextInput(bookmark.text)
    updateUploadedText('')
  }

  const handleGenerateSpeech = async () => {
    if (!currentText.trim()) {
      toast.error('Please enter some text to convert')
      return
    }

    setIsLoading(true)

    try {
      const audioData = await generateSpeech(currentText, voiceSettings)
      setGeneratedAudio({
        ...audioData,
        text: currentText,
        title: `Audio ${Date.now()}`
      })
      setShowAudioModal(true)
    } catch (error) {
      toast.error('Failed to generate speech')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveAudio = (audioData) => {
    addAudioFile(audioData)
    setShowAudioModal(false)
    setGeneratedAudio(null)
  }

  const clearText = () => {
    updateTextInput('')
    updateUploadedText('')
    toast.success('Text cleared!')
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Text Input
        </h1>
        <p className="text-gray-600">
          Type or paste your text below, or upload a text file to convert to speech.
        </p>
      </div>

      {/* Main Text Input */}
      <div className="card mb-8">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Type className="w-5 h-5 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Text Editor
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFileUpload(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload File
            </button>
            <button
              onClick={() => setShowBookmarks(!showBookmarks)}
              className="btn-secondary flex items-center gap-2"
            >
              <Bookmark className="w-4 h-4" />
              Bookmarks ({bookmarks.length})
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <textarea
            value={currentText}
            onChange={handleTextChange}
            placeholder="Enter your text here or upload a file..."
            className="input-field min-h-[300px] resize-y"
            maxLength={10000}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>Characters: {charCount}/10,000</span>
              <span>Words: {wordCount}</span>
              {uploadedText && (
                <span className="badge badge-primary">From uploaded file</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {currentText && (
                <button
                  onClick={clearText}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear
                </button>
              )}
              <button
                onClick={handleGenerateSpeech}
                disabled={!currentText.trim() || isLoading}
                className="btn-primary flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                {isLoading ? 'Generating...' : 'Generate Speech'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bookmark Section */}
      {showBookmarks && (
        <div className="card mb-8">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">
              Voice Bookmarks
            </h3>
          </div>

          {/* Add/Edit Bookmark */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <input
                type="text"
                value={bookmarkTitle}
                onChange={(e) => setBookmarkTitle(e.target.value)}
                placeholder="Bookmark title (optional)"
                className="input-field"
              />
              <button
                onClick={handleBookmarkSave}
                disabled={!currentText.trim()}
                className="btn-primary flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {editingBookmark ? 'Update' : 'Save'} Bookmark
              </button>
              {editingBookmark && (
                <button
                  onClick={() => {
                    setEditingBookmark(null)
                    setBookmarkTitle('')
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Bookmark List */}
          <div className="space-y-3">
            {bookmarks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bookmark className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No bookmarks yet. Save frequently used text for quick access.</p>
              </div>
            ) : (
              bookmarks.map((bookmark) => (
                <div key={bookmark.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">
                      {bookmark.title}
                    </h4>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                      {bookmark.text}
                    </p>
                    <div className="text-xs text-gray-500">
                      Created {new Date(bookmark.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleBookmarkLoad(bookmark)}
                      className="btn-secondary text-sm"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => handleBookmarkEdit(bookmark)}
                      className="btn-secondary text-sm flex items-center gap-1"
                    >
                      <Edit3 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => deleteBookmark(bookmark.id)}
                      className="btn-secondary text-sm text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {showFileUpload && (
        <FileUpload
          onFileUpload={handleFileUpload}
          onClose={() => setShowFileUpload(false)}
        />
      )}

      {/* Audio Modal */}
      {showAudioModal && generatedAudio && (
        <AudioModal
          audioData={generatedAudio}
          onSave={handleSaveAudio}
          onClose={() => {
            setShowAudioModal(false)
            setGeneratedAudio(null)
          }}
        />
      )}
      {/* Pro Tips Section */}
      <div className="mt-16 bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Pro Tips for Natural Sounding Speech</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-700">The Power of Punctuation</h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              Our AI text-to-speech engine relies heavily on punctuation to understand the rhythm and flow of your sentences.
              A comma introduces a short, natural pause, while a period creates a more definitive break.
              Using question marks will cause the voice to rise in pitch at the end of the sentence,
              adding realism to your audio content.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-700">Structuring Your Content</h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              For longer texts, break your content into manageable paragraphs.
              This not only makes it easier for you to manage but also ensures the synthesis engine
              maintains consistent vocal quality throughout the recording.
              Avoid overly long, run-on sentences as they can sometimes lead to unnatural-sounding breath patterns.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-700">Handling Special Characters</h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              While our system is advanced, some special characters or unique acronyms might be pronounced literally.
              If an acronym sounds strange, try writing it out or separating it with dashes (e.g., T-T-S instead of TTS).
              For dates and numbers, ensure they are in a format that is clearly readable within the context.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-primary-700">The Benefits of Bookmarking</h3>
            <p className="text-gray-600 leading-relaxed text-sm">
              If you find yourself converting the same introductory phrases or signature sign-offs frequently,
              save them as bookmarks! This saves time and ensures that your brand's verbal identity stays
              consistent across all your voices and sessions.
            </p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Why Text Preparation Matters</h3>
          <p className="text-gray-700 leading-relaxed">
            Preparing your text correctly is the first step toward high-quality audio output.
            Think of the text input as the "DNA" of your voiceover. By ensuring proper grammar,
            natural phrasing, and clean formatting, you provide the AI with the best possible
            blueprint for generating a voice that is engaging, professional, and clear.
            Whether you're creating a podcast, a video script, or an accessibility tool,
            the effort you put into the text will be reflected in the final audio quality.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <div className="bg-primary-50 px-4 py-2 rounded-lg text-primary-700 text-sm font-medium italic">
              "Great audio starts with great text."
            </div>
            <div className="bg-accent-50 px-4 py-2 rounded-lg text-accent-700 text-sm font-medium italic">
              "Punctuation is the rhythm of speech."
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TextInput
